/**
 * What the major job delete confirmation says and when it lets the operator
 * through. A whole automation used to go on one click of a button that sits
 * next to the task actions, with "nested task jobs will be removed" as the only
 * warning - so the dialog now counts what goes and asks for the name.
 */
import type { MajorJobDeletePreview } from "features/adminUsers/adminUsers";

import { describeSourceReaders } from "./sourceSsnChoice";

/** The phrase the bulk delete asks for (a single delete asks for the name). */
export const BULK_DELETE_PHRASE = "DELETE";

export { confirmPhraseMatches } from "components/common/confirmPhrase";

function counted(n: number, one: string, many: string): string {
  return `${n.toLocaleString("en-US")} ${n === 1 ? one : many}`;
}

/** A shared roster file outlives a deleted job only while another job reads it. */
export function rosterKeptNotice(
  jobIds: number[] | null | undefined,
): string | null {
  if (!jobIds?.length) return null;
  const jobs = jobIds.map((id) => `#${id}`).join(", ");
  return jobIds.length === 1
    ? `The roster file stays: job ${jobs} reads it too.`
    : `The roster file stays: jobs ${jobs} read it too.`;
}

/** What deleting a split major job removes: its split jobs' workspaces; the
 * folders saved to Data Storage are the client's documents and stay. */
export function splitMajorJobDeleteSummary(preview: MajorJobDeletePreview): string {
  const splitJobs = preview.split_jobs ?? 0;
  const saved = preview.split_saved_results ?? 0;
  // The server refuses a task job here; an SSN Job created while a split major
  // job still had that tab goes with it all the same.
  const ssnJob = preview.task_jobs > 0 ? " and its SSN Job" : "";
  if (splitJobs === 0) {
    return `This removes the major job${ssnJob}. It has no split jobs.`;
  }
  const savedLine =
    saved > 0
      ? ` The ${counted(saved, "folder", "folders")} already saved to Data Storage stay.`
      : "";
  return (
    `This removes ${counted(splitJobs, "split job", "split jobs")}${ssnJob}, with ` +
    "their workspace files in S3 (uploaded archives, results, error pages)." +
    savedLine
  );
}

/** The major job's own SSN file goes with it; null when it has none. */
export function ssnFileDeleteNotice(
  preview: MajorJobDeletePreview,
): string | null {
  const file = preview.ssn_file;
  if (!file) return null;
  if (file.objects == null) {
    return (
      "Its SSN file could not be checked just now; it is deleted with it all " +
      "the same."
    );
  }
  if (!file.exists) return null;
  return (
    "Its SSN file is deleted too - earlier versions stay in the storage's " +
    "version history."
  );
}

/** Upload jobs of other major jobs that read its SSN file as their source. */
export function sourceReadersNotice(
  preview: MajorJobDeletePreview,
): string | null {
  const readers = preview.ssn_source_for_jobs ?? [];
  if (!readers.length) return null;
  const one = readers.length === 1;
  return (
    `${one ? "This upload job reads" : "These upload jobs read"} its SSN file as ` +
    `${one ? "its" : "their"} Source SSN file and will match by names until ` +
    `another one is chosen: ${describeSourceReaders(readers)}.`
  );
}

function withSsnNotices(text: string, preview: MajorJobDeletePreview): string {
  return [text, ssnFileDeleteNotice(preview), sourceReadersNotice(preview)]
    .filter(Boolean)
    .join(" ");
}

/** What deleting the major job removes. */
export function majorJobDeleteSummary(preview: MajorJobDeletePreview): string {
  return withSsnNotices(jobsDeleteSummary(preview), preview);
}

function jobsDeleteSummary(preview: MajorJobDeletePreview): string {
  const rows =
    `${counted(preview.task_jobs, "task job", "task jobs")} with ` +
    counted(preview.tasks, "task", "tasks");
  const kept = rosterKeptNotice(preview.roster_kept_for_job_ids);
  const withKept = (text: string) => (kept ? `${text} ${kept}` : text);
  if (preview.log_archives == null || preview.other_files == null) {
    return (
      `This removes ${rows}. Their S3 data could not be counted just now; ` +
      "it is deleted with them all the same."
    );
  }
  if (preview.log_archives === 0 && preview.other_files === 0) {
    return withKept(
      `This removes ${rows}. None of them has anything in S3 yet.`,
    );
  }
  return withKept(
    `This removes ${rows}, and deletes from S3 ` +
      `${counted(preview.log_archives, "task log archive", "task log archives")} and ` +
      `${counted(preview.other_files, "other file", "other files")} of theirs ` +
      "(rosters, matches files, error screenshots).",
  );
}
